import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, Loader2, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

/**
 * Wiederverwendbares Checkout-Modal.
 * Props:
 *   - module: 'ready-check' | 'strategy'
 *   - productName: Anzeige-Name z.B. 'KI Ready Check'
 *   - amountLabel: Anzeige-Betrag z.B. '850 €'
 *   - onClose: () => void
 *   - open: bool
 */
export default function CheckoutModal({ open, onClose, module, productName, amountLabel }) {
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_company: '',
    customer_vat_id: '',
    customer_phone: '',
    terms_accepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Body-Scroll-Lock wenn offen
  useEffect(() => {
    if (open) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = original; };
    }
  }, [open]);

  // UTM aus URL ziehen (DSGVO-konform — nur eigene Parameter)
  const tracking = (() => {
    if (typeof window === 'undefined') return {};
    const params = new URLSearchParams(window.location.search);
    let refPath = '';
    try {
      const ref = document.referrer || '';
      if (ref && ref.startsWith(window.location.origin)) {
        refPath = ref.replace(window.location.origin, '').slice(0, 200);
      }
    } catch { /* noop */ }
    return {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      referrer_path: refPath,
    };
  })();

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.customer_name.trim() || !form.customer_email.trim()) {
      setError('Name und E-Mail sind Pflicht.');
      return;
    }
    if (!form.terms_accepted) {
      setError('Bitte AGB & Datenschutz zustimmen.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/checkout/create`, {
        module,
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim().toLowerCase(),
        customer_company: form.customer_company.trim(),
        customer_vat_id: form.customer_vat_id.trim(),
        customer_phone: form.customer_phone.trim(),
        ...tracking,
      });
      if (data.checkout_url) {
        // Hard-Redirect zu Mollie-Hosted-Checkout
        window.location.href = data.checkout_url;
      } else {
        setError('Bezahlvorgang konnte nicht gestartet werden — versuche es bitte erneut.');
        setLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Bezahlvorgang konnte nicht gestartet werden — versuche es bitte erneut.');
      setLoading(false);
    }
  };

  const openTerms = (e) => { e.preventDefault(); window.dispatchEvent(new Event('openAGB')); };
  const openPrivacy = (e) => { e.preventDefault(); window.dispatchEvent(new Event('openDatenschutz')); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="checkout-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center px-4 py-6 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            className="bg-white w-full max-w-[520px] max-h-[calc(100vh-3rem)] overflow-y-auto"
          >
            {/* Header */}
            <div className="bg-[#0a0a0a] text-white px-6 py-5 flex items-start justify-between">
              <div>
                <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.3em] font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  Direkt buchen
                </p>
                <h3 className="text-lg md:text-xl font-bold uppercase leading-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {productName}
                </h3>
                <p className="text-[#befa0a] text-sm font-bold mt-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {amountLabel} <span className="text-white/40 font-normal text-[11px]">netto, zzgl. 19 % MwSt</span>
                </p>
              </div>
              <button onClick={onClose} disabled={loading}
                className="text-white/60 hover:text-white transition-colors disabled:opacity-40"
                aria-label="Schließen">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={submit} className="p-6 space-y-4">
              <p className="text-[#1f1f1f]/70 text-sm leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Trage deine Daten ein, danach wirst du zur sicheren Bezahlung weitergeleitet.
                Bezahlbar mit Kreditkarte, SEPA-Lastschrift, Klarna und weiteren Methoden.
              </p>

              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Name *</label>
                <input type="text" required value={form.customer_name} disabled={loading}
                  onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))}
                  className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-[#befa0a] disabled:opacity-50"
                  style={{ fontFamily: "'Manrope', sans-serif" }} />
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">E-Mail *</label>
                <input type="email" required value={form.customer_email} disabled={loading}
                  onChange={(e) => setForm((f) => ({ ...f, customer_email: e.target.value }))}
                  className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-[#befa0a] disabled:opacity-50"
                  style={{ fontFamily: "'Manrope', sans-serif" }} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Unternehmen</label>
                  <input type="text" value={form.customer_company} disabled={loading}
                    onChange={(e) => setForm((f) => ({ ...f, customer_company: e.target.value }))}
                    className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-[#befa0a] disabled:opacity-50"
                    style={{ fontFamily: "'Manrope', sans-serif" }} />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">USt-IdNr. (optional)</label>
                  <input type="text" value={form.customer_vat_id} disabled={loading}
                    placeholder="DE…"
                    onChange={(e) => setForm((f) => ({ ...f, customer_vat_id: e.target.value }))}
                    className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-[#befa0a] disabled:opacity-50"
                    style={{ fontFamily: "'Manrope', sans-serif" }} />
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Telefon (optional)</label>
                <input type="tel" value={form.customer_phone} disabled={loading}
                  onChange={(e) => setForm((f) => ({ ...f, customer_phone: e.target.value }))}
                  className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-[#befa0a] disabled:opacity-50"
                  style={{ fontFamily: "'Manrope', sans-serif" }} />
              </div>

              <label className="flex items-start gap-3 cursor-pointer pt-2">
                <input type="checkbox" checked={form.terms_accepted} disabled={loading}
                  onChange={(e) => setForm((f) => ({ ...f, terms_accepted: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 accent-[#befa0a] flex-shrink-0" />
                <span className="text-[#1f1f1f]/70 text-xs leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Ich akzeptiere die{' '}
                  <button type="button" onClick={openTerms} className="text-[#658a07] hover:underline">AGB</button>
                  {' '}und die{' '}
                  <button type="button" onClick={openPrivacy} className="text-[#658a07] hover:underline">Datenschutzerklärung</button>.
                </span>
              </label>

              {error && (
                <p className="text-red-600 text-sm flex items-start gap-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" /> {error}
                </p>
              )}

              <button type="submit" disabled={loading}
                className="w-full bg-[#befa0a] text-[#0a0a0a] py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white hover:border hover:border-[#befa0a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {loading ? <><Loader2 size={16} className="animate-spin" /> Leite weiter…</> : <><Lock size={14} /> Zur Bezahlung ({amountLabel})</>}
              </button>

              <p className="text-[#1f1f1f]/40 text-[11px] text-center pt-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
                <Lock size={10} className="inline mr-1" />
                Bezahlung läuft verschlüsselt über Mollie (deutsches/EU-Rechenzentrum). Wir sehen keine Kartendaten.
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
